import { Injectable } from "@angular/core";
import {
  format,
  addDays,
  subDays,
  isValid,
  isSameDay,
  isBefore,
  isAfter,
  intlFormatDistance,
  parse,
  isMatch,
  toDate,
  subHours,
  addHours,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addYears,
  subYears,
} from "date-fns";
import { BaseService } from "./base.service";
import { LogService } from "./log.service";

@Injectable({ providedIn: "root" })
export class DateService extends BaseService {
  constructor(logService: LogService) {
    super(logService, "DateService");
  }

  public defaultDateFormat = "MM/dd/yyyy";

  public defaultDateTimeFormat = "yyyy-MM-dd hh:mm a";

  public defaultTimeFormat = "hh:mm a";

  public fileDateTimeFormat = "dd-MM-yyyy-HH-mm";

  /**
   * Sets the default date format for the application.
   * This format will be used for formatting date strings when no custom format is provided.
   * @param {string} dateFormat - The date format to be set as the default (e.g., "MM/dd/yyyy").
   * @param {string} sourceFormat - The source format to use for parsing the input string.
   * @example
   * // Set the default date format to "dd-MM-yyyy"
   * setDefaultDateFormat("dd-MM-yyyy");
   */
  public setDefaultDateFormat(dateFormat: string, sourceFormat?: string) {
    if (sourceFormat === "cf") {
      this.defaultDateFormat = this.coldFusionToDateFns(dateFormat);
    } else {
      this.defaultDateFormat = dateFormat;
    }
  }

  /**
   * Sets the default date-time format for the application.
   * This format will be used for formatting date-time strings when no custom format is provided.
   * @param {string} dateFormat - The date-time format to be set as the default (e.g., "yyyy-MM-dd HH:mm:ss").
   * @param {string} sourceFormat - The source format to use for parsing the input string.
   * @example
   * // Set the default date-time format to "yyyy-MM-dd HH:mm:ss"
   * setDefaultDateTimeFormat("yyyy-MM-dd HH:mm:ss");
   */
  public setDefaultDateTimeFormat(dateFormat: string, sourceFormat?: string) {
    if (sourceFormat === "cf") {
      this.defaultDateTimeFormat = this.coldFusionToDateFns(dateFormat);
    } else {
      this.defaultDateTimeFormat = dateFormat;
    }
  }

  /**
   * Sets the default time format for the application.
   * This format will be used for formatting time strings when no custom format is provided.
   * @param {string} timeFormat - The time format to be set as the default (e.g., "HH:mm:ss").
   * @param {string} sourceFormat - The source format to use for parsing the input string.
   * @example
   * // Set the default time format to "HH:mm:ss"
   * setDefaultTimeFormat("HH:mm:ss");
   */
  public setDefaultTimeFormat(timeFormat: string, sourceFormat?: string) {
    if (sourceFormat === "cf") {
      this.defaultTimeFormat = this.coldFusionToDateFns(timeFormat);
    } else {
      this.defaultTimeFormat = timeFormat;
    }
  }

  /**
   * Converts a ColdFusion date format string to a date-fns compatible format string.
   *
   * This function maps ColdFusion-specific date format tokens to their equivalent
   * in date-fns format tokens. It uses a predefined set of mappings and performs
   * replacements in the given ColdFusion format string.
   * @param {string} coldFusionFormat - The ColdFusion date format string to be converted.
   * @returns {string} - The converted date format string compatible with date-fns.
   * @example
   * // Convert a ColdFusion date format to date-fns format
   * const dateFnsFormat = coldFusionToDateFns("YYYY-mm-dd");
   * console.log(dateFnsFormat); // Outputs: "RRRR-MM-dd"
   */
  public coldFusionToDateFns(coldFusionFormat: string): string {
    const formatEntires = [
      ["EEEE", "iiii"], // Day of the week as its full name
      ["EEE", "iii"], // Day of the week as a three-letter abbreviation
      ["mmmm", "MMMM"], // Month as its full name
      ["mmm", "MMM"], // Month as a three-letter abbreviation
      ["mm", "MM"], // Month as digits; leading zero
      ["m", "M"], // Month as digits; no leading zero
      ["YYYY", "RRRR"], // Week year represented by four digits
      ["YY", "RR"], // Week year as last two digits; leading zero for years less than 10
      ["Y", "R"], // Week year
      ["nn", "mm"], // Minutes; leading zero
      ["n", "m"], // Minutes; no leading zero
      ["l", "S"], // Milliseconds
      ["L", "S"], // Milliseconds (alternative)
      ["tt", "aaa"], // AM/PM marker (multiple characters)
      ["t", "a"], // AM/PM marker (single character)
    ];
    return formatEntires.reduce((acc: any, [key, value]: any) => {
      const regex = new RegExp(key);
      return acc.replace(regex, value);
    }, coldFusionFormat);
  }

  /**
   * Converts a Moment.js date format string to a date-fns compatible format string.
   * This function maps Moment.js-specific date format tokens to their equivalent
   * date-fns format tokens. It uses a predefined set of mappings and performs
   * replacements in the given Moment.js format string.
   * @param {string} momentFormat - The Moment.js date format string to be converted.
   * @returns {string} - The converted date format string compatible with date-fns.
   * @example
   * // Convert a Moment.js date format to date-fns format
   * const dateFnsFormat = momentToDateFns("YYYY-MM-DD A");
   * console.log(dateFnsFormat); // Outputs: "yyyy-MM-dd a"
   */
  public momentToDateFns(momentFormat: string): string {
    const formatEntires = [
      ["Y", "y"],
      ["D", "d"],
      ["A", "a"],
      ["T", "a"],
      ["t", "a"],
    ];
    return formatEntires.reduce((acc: any, [key, value]: any) => {
      const regex = new RegExp(key, "g");
      return acc.replace(regex, value);
    }, momentFormat);
  }

  public getFileFormattedTimeString(date?: Date | number): string {
    return this.format(date || Date.now(), this.fileDateTimeFormat);
  }

  /**
   * Formats a given date, timestamp, or date string into a formatted string based on the specified format.
   *
   * If no date is provided, the current date and time is used. The format string can either be a Moment.js
   * format (which is converted to a date-fns compatible format) or defaults to the application's default date format.
   * @param {Date | number | string} [date] - The date, timestamp, or string to format. If omitted, the current date is used.
   * @param {string} [strFormat] - An optional format string (Moment.js style). If not provided, the default date format is used.
   * @returns {string} - The formatted date string.
   * @example
   * // Format the current date using the default format
   * const formattedDate = formatDate();
   * console.log(formattedDate); // Outputs current date in the default format
   * @example
   * // Format a specific date using a custom format
   * const formattedDate = formatDate("2023-12-25", "YYYY/MM/DD");
   * console.log(formattedDate); // Outputs: "2023/12/25"
   */
  public formatDate(date?: Date | number | string, strFormat?: string): string {
    const res = format(
      this.parse(date || Date.now()),
      this.momentToDateFns(strFormat || this.defaultDateFormat),
    );
    return res;
  }

  /**
   * Formats a given date, timestamp, or date string into a formatted string based on the specified format.
   * If no date is provided, the current date and time is used. The format string can either be a Moment.js
   * format (which is converted to a date-fns compatible format) or defaults to the application's default date format.
   * @param {Date | number | string} [date] - The date, timestamp, or string to format. If omitted, the current date is used.
   * @param {string} [strFormat] - An optional format string (Moment.js style). If not provided, the default date format is used.
   * @returns {string} - The formatted date string.
   * @example
   * // Format the current date using the default format
   * const formattedDate = formatTime();
   * console.log(formattedDate); // Outputs current date in the default format
   * @example
   * // Format a specific date using a custom format
   * const formattedDate = formatTime("12:00pm", "HH:mm:ss");
   * console.log(formattedDate); // Outputs: "12:00:00"
   */
  public formatTime(date?: Date | number | string, strFormat?: string): string {
    return format(
      this.parse(date || Date.now()),
      this.momentToDateFns(strFormat || this.defaultTimeFormat),
    );
  }

  /**
   * Formats a given date, timestamp, or date string into a formatted string based on the specified format.
   * If no date is provided, the current date and time is used. The format string can either be a Moment.js
   * format (which is converted to a date-fns compatible format) or defaults to the application's default date format.
   * @param {Date | number | string} [date] - The date, timestamp, or string to format. If omitted, the current date is used.
   * @param {string} [strFormat] - An optional format string (Moment.js style). If not provided, the default date format is used.
   * @returns {string} - The formatted date string.
   * @example
   * // Format the current date using the default format
   * const formattedDate = formatDateTime();
   * console.log(formattedDate); // Outputs current date in the default format
   * @example
   * // Format a specific date using a custom format
   * const formattedDate = formatDateTime("2023-12-25T00:00:00.000Z", "YYYY/MM/DD hh:mm a");
   * console.log(formattedDate); // Outputs: "2023/12/25 12:00 AM"
   */
  public formatDateTime(
    date?: Date | number | string,
    strFormat?: string,
  ): string {
    return format(
      this.parse(date || Date.now()),
      this.momentToDateFns(strFormat || this.defaultDateTimeFormat),
    );
  }

  public format(date: Date | number, strFormat: string): string {
    return format(date, this.momentToDateFns(strFormat));
  }

  /**
   * Formats a given string (expected to represent a date or time) according to the specified format.
   * The function trims the input string, attempts to parse it into a Date object, and formats it
   * using the provided format or defaults. If parsing fails, it returns the original string.
   * @param {string} str - The input string to be formatted. It is expected to represent a date or time.
   * @param {string} [strFormat] - An optional string format to use for formatting the date. If not provided,
   * the default date or time format will be used based on the `parseTime` flag.
   * @param {boolean} [parseTime] - If `true`, the function will use the default time format.
   * If `false`, it will use the default date format unless a custom format is provided.
   * @param {string} [sourceFormat] - An optional source format to use for parsing the input string.
   * @returns {string} - The formatted string if the input could be parsed as a valid date. Otherwise,
   * it returns the original input string.
   * @example
   * // Example usage:
   * formatString("12/25/2023"); // Returns a formatted date using the default date format
   * formatString("15:30", undefined, true); // Returns a formatted time using the default time format
   * formatString("invalid-date"); // Returns "invalid-date" as it cannot be parsed
   */
  public formatString = (
    str: string,
    strFormat?: string,
    parseTime: boolean = false,
    sourceFormat?: string,
  ): string => {
    const trimmed = str.trim();
    if (trimmed) {
      const date = this.parse(trimmed, sourceFormat);
      if (this.isValid(date)) {
        const res = this.format(
          date,
          strFormat ||
            (parseTime ? this.defaultTimeFormat : this.defaultDateFormat),
        );
        return res;
      }
    }
    return str;
  };

  /**
   * Parses a given date string or timestamp into a Date object.
   * If the input is a string, it checks if it matches any of the predefined date or time formats
   * and parses it accordingly. If no format matches, it falls back to converting the input
   * using `this.toDate()`. If the input is a number (timestamp), it is directly passed to `this.toDate()`.
   * @param {string | number | Date} date - The date string or timestamp to be parsed. If a string,
   * @param {string} [strFormat] - An optional string format to use for parsing the date. If provided,
   * it is matched against predefined date and time formats. If a number, it is treated as a timestamp.
   * @returns {Date} - A `Date` object parsed from the input string or number. If the input
   * does not match any format, it falls back to `this.toDate(date)`.
   * @example
   * // Example usage:
   * parse("12/25/2023"); // Parses as MM/dd/yyyy
   * parse(1672531199000); // Parses timestamp to Date
   */
  public parse(date: string | number | Date, strFormat?: string): Date {
    const formats: string[] = [
      ...(strFormat ? [this.momentToDateFns(strFormat)] : []),
      this.defaultDateFormat,
      "MM/dd/yyyy",
      "MM-dd-yyyy",
      "dd/MM/yyyy",
      "dd-MM-yyyy",
      "yyyy-MM-dd",
      "yyyy/MM/dd",
      this.defaultTimeFormat,
      "HH:mm",
      "HH:mm:ss",
      "hh:mm a",
    ];
    const matchingFormat =
      typeof date === "string" &&
      formats.find((dateFormat: string) => isMatch(date, dateFormat));
    return matchingFormat
      ? parse(date, matchingFormat, Date.now())
      : this.toDate(date);
  }

  public addHours(date: Date | number | string, amount: number): Date {
    return addHours(date, amount);
  }

  public subHours(date: Date | number | string, amount: number): Date {
    return subHours(date, amount);
  }

  public addDays(date: Date | number, amount: number): Date {
    return addDays(date, amount);
  }

  public subDays(date: Date | number | string, amount: number): Date {
    return subDays(date, amount);
  }

  public addWeeks(date: Date | number | string, amount: number): Date {
    return addWeeks(date, amount);
  }

  public subWeeks(date: Date | number | string, amount: number): Date {
    return subWeeks(date, amount);
  }

  public addMonths(date: Date | number | string, amount: number): Date {
    return addMonths(date, amount);
  }

  public subMonths(date: Date | number | string, amount: number): Date {
    return subMonths(date, amount);
  }

  public addYears(date: Date | number | string, amount: number): Date {
    return addYears(date, amount);
  }

  public subYears(date: Date | number | string, amount: number): Date {
    return subYears(date, amount);
  }

  /**
   * Returns false if argument is Invalid Date and true otherwise.
   * Argument is converted to Date using `toDate`. See [toDate](https://date-fns.org/docs/toDate)
   * Invalid Date is a Date, whose time value is NaN.
   * Time value of Date: http://es5.github.io/#x15.9.1.1
   * @param date - The date to check
   * @returns The date is valid
   * @example
   * // For the valid date:
   * const result = isValid(new Date(2014, 1, 31))
   * //=> true
   */
  public isValid(date: unknown): boolean {
    return isValid(date);
  }

  /**
   * Are the given dates in the same day (and year and month)?
   * @param laterDate - The first date to check
   * @param earlierDate - The second date to check
   * @returns The dates are in the same day (and year and month)
   * @example
   * // Are 4 September 06:00:00 and 4 September 18:00:00 in the same day?
   * const result = isSameDay(new Date(2014, 8, 4, 6, 0), new Date(2014, 8, 4, 18, 0))
   * //=> true
   */
  public isSameDay(
    laterDate: Date | number | string,
    earlierDate: Date | number | string,
  ): boolean {
    return isSameDay(laterDate, earlierDate);
  }

  /**
   * Is the first date before the second one?
   * @param date - The date that should be before the other one to return true
   * @param dateToCompare - The date to compare with
   * @returns The first date is before the second date
   * @example
   * // Is 10 July 1989 before 11 February 1987?
   * const result = isBefore(new Date(1989, 6, 10), new Date(1987, 1, 11))
   * //=> false
   */
  public isBefore(
    date: Date | number | string,
    dateToCompare: Date | number | string,
  ): boolean {
    return isBefore(date, dateToCompare);
  }

  /**
   * Is the first date after the second one?
   * @param date - The date that should be after the other one to return true
   * @param dateToCompare - The date to compare with
   * @returns The first date is after the second date
   * @example
   * // Is 10 July 1989 after 11 February 1987?
   * const result = isAfter(new Date(1989, 6, 10), new Date(1987, 1, 11))
   * //=> true
   */
  public isAfter(
    date: Date | number | string,
    dateToCompare: Date | number | string,
  ): boolean {
    return isAfter(date, dateToCompare);
  }

  /**
   * Convert the given argument to an instance of Date.
   *
   * If the argument is an instance of Date, the function returns its clone.
   *
   * If the argument is a number, it is treated as a timestamp.
   *
   * If the argument is none of the above, the function returns Invalid Date.
   *
   * Starting from v3.7.0, it clones a date using `[Symbol.for("constructDateFrom")]`
   * enabling to transfer extra properties from the reference date to the new date.
   * It's useful for extensions like [`TZDate`](https://github.com/date-fns/tz)
   * that accept a time zone as a constructor argument.
   * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
   * @param date - The value to convert
   * @returns The parsed date in the local time zone
   * @example
   * // Clone the date:
   * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
   * //=> Tue Feb 11 2014 11:30:30
   */
  public toDate(date: Date | number | string): Date {
    return toDate(date);
  }

  /**
   * The function calculates the difference between two dates and formats it as a human-readable string.
   *
   * The function will pick the most appropriate unit depending on the distance between dates. For example, if the distance is a few hours, it might return `x hours`. If the distance is a few months, it might return `x months`.
   *
   * You can also specify a unit to force using it regardless of the distance to get a result like `123456 hours`.
   *
   * See the table below for the unit picking logic:
   *
   * | Distance between dates | Result (past)  | Result (future) |
   * | ---------------------- | -------------- | --------------- |
   * | 0 seconds              | now            | now             |
   * | 1-59 seconds           | X seconds ago  | in X seconds    |
   * | 1-59 minutes           | X minutes ago  | in X minutes    |
   * | 1-23 hours             | X hours ago    | in X hours      |
   * | 1 day                  | yesterday      | tomorrow        |
   * | 2-6 days               | X days ago     | in X days       |
   * | 7 days                 | last week      | next week       |
   * | 8 days-1 month         | X weeks ago    | in X weeks      |
   * | 1 month                | last month     | next month      |
   * | 2-3 months             | X months ago   | in X months     |
   * | 1 quarter              | last quarter   | next quarter    |
   * | 2-3 quarters           | X quarters ago | in X quarters   |
   * | 1 year                 | last year      | next year       |
   * | 2+ years               | X years ago    | in X years      |
   * @param laterDate - The date
   * @param earlierDate - The date to compare with, if not provided defaults to the current date.
   * @param options - An object with options.
   * See MDN for details [Locale identification and negotiation](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locale_identification_and_negotiation)
   * The narrow one could be similar to the short one for some locales.
   * @returns The distance in words according to language-sensitive relative time formatting.
   * @example
   * // What is the distance between the dates when the fist date is after the second?
   * intlFormatDistance(
   *   new Date(1986, 3, 4, 11, 30, 0),
   *   new Date(1986, 3, 4, 10, 30, 0)
   * )
   * //=> 'in 1 hour'
   */
  public intlFormatDistance(
    laterDate: Date | number | string,
    earlierDate?: Date | number | string,
    options: object = {},
  ): string {
    return intlFormatDistance(laterDate, earlierDate || Date.now(), options);
  }
}
